// Copyright (c) Ping. All rights reserved.
//
// Voice menu — pixel-matched to Figma DS "Voice menu / desktop" (4389:98465 +
// submenu 4389:98346):
//   • Input Device   (subtitle = active mic)     → device submenu (right)
//   • Output Device  (subtitle = active speaker)  → device submenu (right)
//   • divider
//   • Input Volume   (slider)
//   • Voice settings (gear)
// Each device row opens its list as an AnchoredPopover anchored to the RIGHT of
// the row (Figma's side panel), so it's body-portaled with the correct z-index
// and flips/clamps when there's no room. Device switching is live via LiveKit's
// switchActiveDevice (store.switchDevice). Tailwind-first with tokens; light-mode
// mirror. No leaks: the device probe is abort-guarded.

import { useEffect, useRef, useState } from 'react';
import { Pressable, Text, View } from 'react-native';

import { Check, ChevronRight, Mic, Settings } from 'lucide-react-native';

import { useCallStore } from '@calls/store/callStore';

import { AnchoredPopover } from './AnchoredPopover';
import { useCallIconColors } from './callTheme';

function OptionRow({
  title,
  subtitle,
  icon,
  onPress,
  selected = false,
}: {
  title: string;
  subtitle?: string;
  icon: JSX.Element;
  onPress?: () => void;
  selected?: boolean;
}): JSX.Element {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={title}
      className={`min-h-[45px] flex-row items-center gap-[12px] rounded-[8px] px-[12px] py-[4px] active:bg-black/[0.04] dark:active:bg-white/[0.06] ${
        selected ? 'bg-black/[0.04] dark:bg-white/[0.08]' : ''
      }`}
    >
      <View className="min-w-0 flex-1 flex-col">
        <Text className="text-[14px] leading-[20px] text-light-text-primary dark:text-[#F5F6F8]" numberOfLines={1}>
          {title}
        </Text>
        {subtitle !== undefined ? (
          <Text className="text-[12px] leading-[17px] text-light-text-secondary dark:text-[#B5BAC3]" numberOfLines={1}>
            {subtitle}
          </Text>
        ) : null}
      </View>
      {icon}
    </Pressable>
  );
}

function DeviceSubmenu({
  devices,
  selectedId,
  onChoose,
}: {
  devices: MediaDeviceInfo[];
  selectedId: string;
  onChoose: (id: string) => void;
}): JSX.Element {
  return (
    <View className="w-[253px] rounded-[12px] border border-light-border-secondary bg-light-surface-primary p-[4px] dark:border-dark-surface-pressed dark:bg-dark-surface-panel">
      {devices.length === 0 ? (
        <Text className="px-[12px] py-[8px] text-[14px] text-light-text-muted dark:text-[#B5BAC3]">No devices</Text>
      ) : (
        devices.map((d) => (
          <Pressable
            key={d.deviceId}
            onPress={() => onChoose(d.deviceId)}
            className="min-h-[45px] flex-row items-center justify-between gap-[8px] rounded-[8px] px-[12px] py-[4px] active:bg-black/[0.04] dark:active:bg-white/[0.06]"
          >
            <Text numberOfLines={1} className="flex-1 text-[14px] text-light-text-primary dark:text-[#F5F6F8]">
              {d.label || 'Default'}
            </Text>
            {selectedId === d.deviceId ? <Check size={16} color="#537EFF" /> : null}
          </Pressable>
        ))
      )}
    </View>
  );
}

export function VoiceMenu({ onClose }: { onClose: () => void }): JSX.Element {
  const ic = useCallIconColors();
  const [openSub, setOpenSub] = useState<null | 'audioinput' | 'audiooutput'>(null);
  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [spkDevices, setSpkDevices] = useState<MediaDeviceInfo[]>([]);
  const [micId, setMicId] = useState('');
  const [spkId, setSpkId] = useState('');
  const [volume, setVolume] = useState(0.8);

  // Anchors for the right-side submenus.

  const micRowRef = useRef<any>(null);

  const spkRowRef = useRef<any>(null);

  useEffect(() => {
    let alive = true;
    const store = useCallStore.getState();
    void Promise.all([store.listDevices('audioinput'), store.listDevices('audiooutput')]).then(([mics, spks]) => {
      if (!alive) return;
      setMicDevices(mics);
      setSpkDevices(spks);
      setMicId((cur) => (cur === '' && mics[0] ? mics[0].deviceId : cur));
      setSpkId((cur) => (cur === '' && spks[0] ? spks[0].deviceId : cur));
    });
    return () => {
      alive = false;
    };
  }, []);

  const label = (list: MediaDeviceInfo[], id: string): string =>
    list.find((d) => d.deviceId === id)?.label || 'System Default';

  const choose = (kind: 'audioinput' | 'audiooutput', id: string): void => {
    if (kind === 'audioinput') setMicId(id);
    else setSpkId(id);
    void useCallStore.getState().switchDevice(kind, id);
    setOpenSub(null);
  };

  return (
    <View className="w-[253px] gap-[4px] rounded-[12px] border border-light-border-secondary bg-light-surface-primary p-[4px] dark:border-dark-surface-pressed dark:bg-dark-surface-panel">
      <View ref={micRowRef}>
        <OptionRow
          title="Input Device"
          subtitle={label(micDevices, micId)}
          selected={openSub === 'audioinput'}
          onPress={() => setOpenSub((s) => (s === 'audioinput' ? null : 'audioinput'))}
          icon={<ChevronRight size={16} strokeWidth={1.5} color={ic.muted} />}
        />
      </View>
      <View ref={spkRowRef}>
        <OptionRow
          title="Output Device"
          subtitle={label(spkDevices, spkId)}
          selected={openSub === 'audiooutput'}
          onPress={() => setOpenSub((s) => (s === 'audiooutput' ? null : 'audiooutput'))}
          icon={<ChevronRight size={16} strokeWidth={1.5} color={ic.muted} />}
        />
      </View>

      <View className="my-[2px] h-px bg-light-border-secondary dark:bg-dark-surface-elevated" />

      <View className="gap-[6px] px-[12px] py-[6px]">
        <Text className="text-[14px] leading-[20px] text-light-text-primary dark:text-[#F5F6F8]">Input Volume</Text>
        <View className="flex-row items-center gap-[8px]">
          <Mic size={14} strokeWidth={1.5} color={ic.muted} />
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(volume * 100)}
            onChange={(e) => setVolume(Number(e.target.value) / 100)}
            aria-label="Input volume"
            style={{ flex: 1, accentColor: '#537EFF', cursor: 'pointer' }}
          />
        </View>
      </View>

      <OptionRow
        title="Voice settings"
        onPress={onClose}
        icon={<Settings size={18} strokeWidth={1.5} color={ic.muted} />}
      />

      {/* Right-anchored device submenus (Figma side panel). */}
      <AnchoredPopover
        anchorRef={micRowRef}
        open={openSub === 'audioinput'}
        onClose={() => setOpenSub(null)}
        placement="right"
        align="start"
      >
        <DeviceSubmenu devices={micDevices} selectedId={micId} onChoose={(id) => choose('audioinput', id)} />
      </AnchoredPopover>
      <AnchoredPopover
        anchorRef={spkRowRef}
        open={openSub === 'audiooutput'}
        onClose={() => setOpenSub(null)}
        placement="right"
        align="start"
      >
        <DeviceSubmenu devices={spkDevices} selectedId={spkId} onChoose={(id) => choose('audiooutput', id)} />
      </AnchoredPopover>
    </View>
  );
}
